/**
 * useSolutions
 *
 * Handles solution switching, field editing (debounced save with version tracking),
 * and solution creation for sub-projects.
 */

import { useCallback, useRef, useEffect } from 'react';
import type { Solution as SolutionModel } from '../services/subProjectService';
import { solutionService } from '../services/solutionService';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/ToastProvider';

export interface UseSolutionsResult {
  handleSolutionChange: (solutionId: string) => void;
  handleSolutionFieldChange: (fieldName: string, value: string) => void;
  handleAddSolution: () => Promise<void>;
}

export function useSolutions(
  entityId: string | undefined,
  solutions: SolutionModel[],
  setSolutions: React.Dispatch<React.SetStateAction<SolutionModel[]>>,
  activeSolutionId: string | null,
  setActiveSolutionId: React.Dispatch<React.SetStateAction<string | null>>,
  setHighlightedFields: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
): UseSolutionsResult {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const saveTimeoutRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const solutionsRef = useRef<SolutionModel[]>([]);
  useEffect(() => { solutionsRef.current = solutions; }, [solutions]);

  const handleSolutionChange = useCallback((solutionId: string) => {
    setActiveSolutionId(solutionId);
    if (entityId) {
      localStorage.setItem(`rda_active_solution_${entityId}`, solutionId);
    }
  }, [entityId, setActiveSolutionId]);

  const handleSolutionFieldChange = useCallback((fieldName: string, value: string) => {
    if (!activeSolutionId || !entityId) return;
    const capturedSolutionId = activeSolutionId;
    setSolutions(prev => prev.map(s => s.id === capturedSolutionId ? { ...s, [fieldName]: value } : s));
    setHighlightedFields(prev => ({ ...prev, [fieldName]: true }));

    if (saveTimeoutRef.current[`sol_${fieldName}`]) {
      clearTimeout(saveTimeoutRef.current[`sol_${fieldName}`]);
    }
    saveTimeoutRef.current[`sol_${fieldName}`] = setTimeout(async () => {
      const sol = solutionsRef.current.find(s => s.id === capturedSolutionId);
      if (!sol) return;
      try {
        const updated = await solutionService.update(entityId, capturedSolutionId, { [fieldName]: value }, sol.version);
        setSolutions(prev => prev.map(s => s.id === capturedSolutionId ? { ...s, version: updated.version } : s));
      } catch (err) {
        console.error(`Failed to save solution field ${fieldName}:`, err);
      }
    }, 2000);
  }, [activeSolutionId, entityId, setSolutions, setHighlightedFields]);

  const handleAddSolution = useCallback(async () => {
    if (!entityId) return;
    try {
      const { solution, warn_many } = await solutionService.create(entityId);
      setSolutions(prev => [...prev, solution]);
      setActiveSolutionId(solution.id);
      localStorage.setItem(`rda_active_solution_${entityId}`, solution.id);
      if (warn_many) {
        addToast({ type: 'warning', title: t('scv2.tooManySolutions', 'Many solutions'), message: t('scv2.tooManySolutionsMsg', 'You now have more than 5 solutions. Consider merging some.') });
      }
    } catch {
      addToast({ type: 'error', title: 'Error', message: 'Failed to create solution.' });
    }
  }, [entityId, setSolutions, setActiveSolutionId, addToast, t]);

  return {
    handleSolutionChange,
    handleSolutionFieldChange,
    handleAddSolution,
  };
}
